import {
    type FC,
    createContext,
    useContext,
    useState,
    useLayoutEffect,
    type ReactNode,
} from 'react';
import { type PageType, pageTypeGuard } from '../pageType';
import serverVars from 'server-vars';

type PageTypeContextValue = [
    pageType: PageType | undefined,
    setPageType: ((arg: PageType) => void) | undefined
];

const PageTypeContext = createContext<PageTypeContextValue>([undefined, undefined]);

export const usePageType = (): PageTypeContextValue[0] => useContext(PageTypeContext)[0];

export const useSetPageType = (): PageTypeContextValue[1] => useContext(PageTypeContext)[1];

const hashPageType = (typeof window !== 'undefined' && window.location.hash.replace('#', '')) || '';
const serverPageType = serverVars.get('pageType');

// update pageType after initial render to match SSR
const HashPageTypeHandler: FC<{ children: ReactNode }> = ({ children }) => {
    const setPageType = useSetPageType();
    useLayoutEffect(() => {
        if (pageTypeGuard(hashPageType) && hashPageType !== serverPageType) {
            setPageType?.(hashPageType);
        }
    }, [setPageType]);
    return <>{children}</>;
};

export const PageTypeProvider: FC<{ initialPageType?: string; children: ReactNode }> = ({
    initialPageType,
    children,
}) => {
    const ancestorProvider = useContext(PageTypeContext)[0];
    if (process.env.NODE_ENV !== 'production' && ancestorProvider) {
        throw Error(
            'There can be only one! Please ensure there is only one PageTypeProvider per React render.'
        );
    }

    let safePageType = initialPageType || serverPageType;

    if (!pageTypeGuard(safePageType)) {
        safePageType = 'featured';
    }

    const [pageType, setPageType] = useState(safePageType);
    return (
        <PageTypeContext.Provider value={[pageType, setPageType]}>
            {children}
        </PageTypeContext.Provider>
    );
};

export const ClientPageTypeProvider: FC<{ children: ReactNode }> = ({ children }) => (
    <PageTypeProvider>
        <HashPageTypeHandler>{children}</HashPageTypeHandler>
    </PageTypeProvider>
);
